import { useEffect, useRef, useState } from 'react';
import { trackingService } from '@/lib/tracking';
import {
  getPreliminaryOffers,
  hasPreliminaryIncomeRejection,
} from '@/lib/offerUtils';
import {
  CreditProductType,
  PreliminaryOfferStatus,
  type CreditOffer,
} from '../types/offer';
import type { OceanOfferType } from '../components/OceanOfferFace';
import { useLoanOffer } from './useLoanOffer';

// The BC score is generated in the background after the phone code, or
// re-requested by the backend when it is too old for an offer (a new BC
// interrogation takes a few minutes); the preliminary offer itself is priced
// synchronously once the score exists.
const SCORE_RETRY_MS = 5000;
const SCORE_RETRY_ATTEMPTS = 60;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Why no preliminary offer is shown: a step the backend still misses, no BC
// score to price on (no credit history), an offer rejected on income or debt
// ratio, or no offer for this user.
export type PreliminaryOfferOutcome =
  | 'final-offer-exists'
  | 'score-required'
  | 'bc-terms-required'
  | 'income-required'
  | 'no-score'
  | 'income-rejected'
  | 'not-available';

const OUTCOMES: Partial<Record<PreliminaryOfferStatus, PreliminaryOfferOutcome>> = {
  [PreliminaryOfferStatus.FinalOfferExists]: 'final-offer-exists',
  [PreliminaryOfferStatus.ScoreRequired]: 'score-required',
  [PreliminaryOfferStatus.BcTermsRequired]: 'bc-terms-required',
  [PreliminaryOfferStatus.IncomeRequired]: 'income-required',
  [PreliminaryOfferStatus.NoBcScore]: 'no-score',
};

export interface OceanOffer {
  id: string;
  type: OceanOfferType;
  amount: number;
  numberOfRates: number;
}

const OCEAN_OFFER_TYPES: Partial<Record<CreditOffer['type'], OceanOfferType>> = {
  [CreditProductType.WhiteLabelCard]: 'credit-line',
  [CreditProductType.Consumer]: 'installment',
};

// The partner offers as cards, credit line first as in the design.
const toOceanOffers = (offers: CreditOffer[]): OceanOffer[] =>
  offers
    .flatMap((offer) => {
      const type = OCEAN_OFFER_TYPES[offer.type];
      return type
        ? [
            {
              id: offer.id,
              type,
              amount: offer.creditLimit,
              numberOfRates: offer.maxNumberOfRates,
            },
          ]
        : [];
    })
    .sort((a, b) =>
      a.type === b.type ? 0 : a.type === 'credit-line' ? -1 : 1
    );

interface UsePreliminaryOfferGenerationOptions {
  // False while the generation must not start (KYC loading or already valid).
  isEnabled: boolean;
  onOutcome: (outcome: PreliminaryOfferOutcome) => void;
}

/**
 * Generates the preliminary offer priced on the BC score and declared income,
 * waiting for the score when it is not ready yet, and reads it once priced.
 */
export const usePreliminaryOfferGeneration = ({
  isEnabled,
  onOutcome,
}: UsePreliminaryOfferGenerationOptions) => {
  const {
    loanOfferState,
    isLoading,
    getLoanOffersState,
    requestPreliminaryOffer,
  } = useLoanOffer(true);
  const [hasGenerationFailed, setHasGenerationFailed] = useState(false);
  const [generationRun, setGenerationRun] = useState(0);
  const hasTrackedOffer = useRef(false);
  const onOutcomeRef = useRef(onOutcome);
  onOutcomeRef.current = onOutcome;

  const preliminaryOffers = getPreliminaryOffers(loanOfferState?.creditOffers);
  const hasOffers = preliminaryOffers.length > 0;

  useEffect(() => {
    if (!isEnabled || isLoading || hasOffers) {
      return;
    }

    let isCancelled = false;

    const generate = async () => {
      setHasGenerationFailed(false);

      for (let attempt = 0; attempt < SCORE_RETRY_ATTEMPTS; attempt++) {
        const status = await requestPreliminaryOffer()
          .then((response) => response.status)
          .catch(() => null);
        if (isCancelled) return;

        if (status === null) {
          setHasGenerationFailed(true);
          return;
        }

        if (status === PreliminaryOfferStatus.ScoreNotReady) {
          await wait(SCORE_RETRY_MS);
          if (isCancelled) return;
          continue;
        }

        void trackingService.trackEvent('preliminary_offer_request', { status });

        const outcome = OUTCOMES[status];
        if (outcome) {
          onOutcomeRef.current(outcome);
          return;
        }

        const isGenerated =
          status === PreliminaryOfferStatus.Generated ||
          status === PreliminaryOfferStatus.AlreadyGenerated;
        if (isGenerated) {
          const state = await getLoanOffersState();
          if (isCancelled) return;
          // Rendered from the refreshed query data.
          if (getPreliminaryOffers(state?.creditOffers).length > 0) return;
          if (hasPreliminaryIncomeRejection(state?.creditOffers)) {
            onOutcomeRef.current('income-rejected');
            return;
          }
        }

        onOutcomeRef.current('not-available');
        return;
      }

      // The score never arrived: a technical problem, not a missing score.
      void trackingService.trackEvent('preliminary_offer_request', {
        status: 'ScoreTimeout',
      });
      setHasGenerationFailed(true);
    };

    void generate();
    return () => {
      isCancelled = true;
    };
  }, [isEnabled, isLoading, hasOffers, generationRun]);

  useEffect(() => {
    if (!hasOffers || hasTrackedOffer.current) {
      return;
    }

    hasTrackedOffer.current = true;
    void trackingService.trackEvent('preliminary_offer_shown', {
      offers_count: preliminaryOffers.length,
    });
  }, [hasOffers]);

  return {
    oceanOffers: toOceanOffers(preliminaryOffers),
    hasOffers,
    hasGenerationFailed,
    isLoading,
    retry: () => setGenerationRun((run) => run + 1),
  };
};
